/* THE UNITS TABLE — `GET /api/tam/units`, read once, pure. Zero imports.
 *
 * 218 rows of `sport × level → { median, mean, p25, min, max, n }`, backtested
 * against production on 2026-10-01: 0 rows missing `n`, 0 serving a median of 0,
 * 51 unmeasured rows each carrying a reason, 0 rows carrying a price. Total
 * reconciles at 15,105.
 *
 * ⭐ THIS IS WHAT UNBLOCKS RANKING THE WHOLE BOOK BY MONEY. The per-record route
 * `/api/tam/record/:id` is one call per record, and books run 339 to 2,225 — so
 * ordering a book by dollars was impossible with it and I refused to fake it by
 * re-ordering the 30 rows urgency had already chosen. One cacheable call over
 * sport × level gives every record a figure.
 *
 * ⚠️ `Football/High School` → median 62, n 69. 62 × $288 = $17,856, inside the
 * $17,280–$43,200 band from Brian's worked example (min 2 … max 180 × $288 =
 * $576 … $51,840 at the extremes). The example is the median case, not the
 * range, which is why the renderer shows the range AND names the median.
 */

/** Trim, collapse whitespace. Case is NOT touched — see the archery note. */
const s = (v) => String(v == null ? '' : v).trim().replace(/\s+/g, ' ')

/**
 * The lookup key for a row.
 *
 * 🔴 CASE IS PRESERVED, DELIBERATELY, AND THERE IS A LIVE DEFECT BEHIND THAT.
 * `Archery` and `ARCHERY` are SEPARATE sports in the snapshot:
 * `Archery/High School` holds 90 records and ZERO measured orders, while
 * `ARCHERY/High School` holds 3 orders with a median of 24 and ZERO records. So
 * 92 real archery championships price as unmeasured while their evidence sits
 * stranded on the uppercase twin.
 *
 * ⚠️ I AM NOT LOWER-CASING THE KEY TO PAPER OVER IT. A client-side normalisation
 * would hide a data defect the backend owns (`tam-sport-crosswalk.js` is on
 * main), and it would hide it in the one place a person might otherwise notice:
 * 90 archery records reading "not measured" is visible, whereas a quietly
 * merged key is not. It would also be a second copy of a crosswalk rule, which
 * is the fault this codebase keeps finding. Render what the endpoint says; the
 * fix lands upstream and this starts working with no change here.
 */
export const unitsKey = (sport, level) => `${s(sport)}||${s(level)}`

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)

/* Where a price might live on the envelope. The ROWS carry none — that was
 * verified — so if the figure is computable at all the price is at the top
 * level. Several plausible spellings are read and the one that answered is
 * REPORTED, the same discipline `CONFIG_KEY_ALIASES` uses in the studio: a
 * missing key degrades to a stated absence rather than to a guess. */
const PRICE_KEYS = ['pricePerUnitUsd', 'pricePerUnit', 'priceUsd', 'price', 'unitPriceUsd']

/**
 * Read the payload into a lookup.
 *
 * Returns `{ rows, measuredAt, priceUsd, priceSource, total, unmeasured }`.
 * `rows` is a Map keyed by `unitsKey`.
 */
export function readUnitsTable(payload, { measuredAtHeader = null, priceHeader = null } = {}) {
  const list = Array.isArray(payload) ? payload
    : Array.isArray(payload?.rows) ? payload.rows
      : Array.isArray(payload?.units) ? payload.units
        : Array.isArray(payload?.data) ? payload.data : []

  const rows = new Map()
  let unmeasured = 0
  for (const r of list) {
    const key = unitsKey(r?.sport, r?.level)
    const n = num(r?.n) ?? num(r?.sampleSize)
    const median = num(r?.median) ?? num(r?.p50)
    /* ⚠️ A ROW WITH NO MEASUREMENT IS NOT A ROW WITH ZERO UNITS. The endpoint
     * serves a `reason` on those and never a 0; both halves are carried through
     * so the renderer can say the sentence rather than draw a blank. */
    const measured = n != null && n > 0 && median != null
    if (!measured) unmeasured += 1
    rows.set(key, {
      sport: s(r?.sport),
      level: s(r?.level),
      measured,
      median,
      mean: num(r?.mean),
      p25: num(r?.p25),
      min: num(r?.min),
      max: num(r?.max),
      n,
      records: num(r?.records) ?? num(r?.recordCount),
      reason: s(r?.reason) || null,
    })
  }

  let priceUsd = null
  let priceSource = null
  if (payload && !Array.isArray(payload)) {
    for (const k of PRICE_KEYS) {
      /* ⚠️ `num(payload[k]?.value)` IS THE ALIAS THAT MATTERS, and the reason
       * it is here is a measured near-miss. `/api/tam` serves the price as
       * `{ usdPerUnit: 288, value: 288 }` while `/api/tam/units` serves a bare
       * `288`. The units rows beside it already accepted `mean` as an alias for
       * `value`, and that ONE accepted alias was the whole reason the units
       * resolved while the price did not — two parsers, each correct on its own
       * side, with nothing crossing the gap. Both spellings are read here. */
      const v = num(payload[k]) ?? num(payload[k]?.value) ?? num(payload[k]?.usdPerUnit)
      if (v != null && v > 0) { priceUsd = v; priceSource = k; break }
    }
  }
  /* 🔴 THE HEADER IS THE FALLBACK, AND IT IS NOT DECORATION. The route's
   * default response is a bare ARRAY — which cannot carry a top-level price at
   * all — and `x-sr-price-per-unit-usd` is served on both shapes. Without this,
   * an envelope that stops arriving degrades to a blank where a dollar figure
   * belongs, which is a failure that renders as a clean screen and ships. The
   * body still wins when it has an answer; `priceSource` names which one
   * answered, so a figure can always be traced to where it came from. */
  if (priceUsd == null && priceHeader != null) {
    const v = num(typeof priceHeader === 'number' ? priceHeader : Number(priceHeader))
    if (v != null && v > 0) { priceUsd = v; priceSource = 'x-sr-price-per-unit-usd' }
  }

  return {
    rows,
    total: list.length,
    unmeasured,
    /* `x-sr-units-measured-at` answers "is this newer than me", so it is kept
     * even when the body also carries it. */
    measuredAt: s(payload?.measuredAt) || s(measuredAtHeader) || null,
    priceUsd,
    priceSource,
  }
}

/**
 * The row for a record, or null when sport × tier is not in the table.
 *
 * 🔴 JOINS ON `segment`, NOT `level`, AND THAT IS THE WHOLE DEFECT. Measured
 * 2026-10-05: of Philip Davanzo's 1,797 assigned records this found FIFTY. His
 * book priced at $301,824 against a >$4M year, and the screen honestly
 * reported "no measured unit history" 1,747 times.
 *
 * The cause is a DIMENSION mismatch, not a spelling one. `championships.js`
 * sets `level` from `sr_level`, and `sr_level` is the SCOPE — State 6,581 /
 * Conference 2,846 / National 2,991. This table is keyed on the TIER
 * ("High School", "College or University"). A scope can never equal a tier, so
 * the join could only ever match by accident — and it did: all 50 hits were
 * records whose `sr_level` was BLANK, colliding with the whole-sport rows the
 * table emits at `level: null`.
 *
 * `segment` is the tier (`sr_segment`: "HS State" 7,854 / "College" 2,103 /
 * "All-Star" 1,370). Joining on it takes one rep from 50 to 1,425 of 1,797,
 * and the company from 521 to 10,794 of 15,151.
 *
 * ⚠️ `level` IS STILL TRIED, SECOND. Some records legitimately carry a tier in
 * `sr_level` ("High School" 27, "College" 16 on the live board), and a few
 * resolve through the blank-level whole-sport rows. Dropping that path would
 * trade one silent miss for another, so it is a documented fallback rather
 * than a deletion.
 *
 * ⚠️ AND STILL NO CLIENT-SIDE NORMALISATION. The vocabulary crosswalk
 * ("HS State" → "High School", "Cheer" → "Cheerleading") is the BACKEND's job
 * and it now ships ALIAS ROWS for exactly this, each carrying `aliasOf`. The
 * rule above this function stands: a merged key here would hide a data defect
 * this file does not own. All that changed is WHICH FIELD is the tier.
 */
export function rowForRecord(table, record) {
  if (!table?.rows) return null
  return table.rows.get(unitsKey(record?.sport, record?.segment))
    || table.rows.get(unitsKey(record?.sport, record?.level))
    || null
}

/**
 * Adapt a units row into the `factors` shape `opportunityValue()` already
 * takes, so the money arithmetic lives in ONE place.
 *
 * ⚠️ THE ADAPTER IS THE POINT. `opportunityValue` is asserted by 31 cases
 * including Brian's worked example and the proof that `fieldSize` and
 * `penetration` cannot leak in. Re-deriving `units × price` here for the table
 * path would be a second copy of the one calculation that decides what a rep
 * quotes, and the two would disagree the first time either changed.
 */
export function chainFromUnitsRow(row, priceUsd) {
  if (!row || !row.measured || priceUsd == null) return null
  return {
    factors: {
      unitsPerBuyer: {
        value: row.mean, median: row.median, min: row.min, max: row.max, n: row.n,
      },
      pricePerUnitUsd: { value: priceUsd, basis: 'forward price per unit' },
    },
  }
}

/**
 * Why a record has no figure, in the endpoint's OWN WORDS where it gave them.
 *
 * ⭐ Brian: *"Render that reason. A rep seeing a blank where a dollar figure
 * should be must read WHY."* 734 records sit on unmeasured sport+level rows —
 * a visible population, not an edge case — and the endpoint already writes the
 * sentence: *"No delivered order has ever been measured for this sport and
 * level, so there is no units basis to quote. This is a gap in what we have
 * measured, NOT a championship worth nothing."*
 *
 * ⚠️ THE SERVER'S SENTENCE IS PREFERRED OVER ANY OF MINE. It wrote it for the
 * person reading it, and paraphrasing it would lose the one thing it is for —
 * the same rule the TAM panel already applies to a 403 detail. Mine are only
 * for the cases the table cannot speak to: a sport × level absent from the
 * table at all, and a table with no price.
 */
export function missingReason(table, record, row) {
  if (!table) {
    return 'The units table has not loaded, so there is no figure yet — not a figure of zero.'
  }
  if (table.priceUsd == null) {
    return 'The units table carries no price per unit, so a dollar figure cannot be computed from '
      + 'it. Nothing is guessed here: the price is the backend\'s to serve.'
  }
  if (!row) {
    return `No row for ${s(record?.sport) || 'this sport'} / ${s(record?.level) || 'this level'} in `
      + 'the units table, so there is nothing measured to price it from. That is a gap in what we '
      + 'have measured, NOT a championship worth nothing.'
  }
  if (row.reason) return row.reason
  return 'No delivered order has ever been measured for this sport and level, so there is no units '
    + 'basis to quote. This is a gap in what we have measured, NOT a championship worth nothing.'
}
